import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import CoreInput, { InputTypes } from 'sharedComponents/core/Input/CoreInput';
import Button from 'components/Button/Button';
import AvatarUpload from 'components/Delegate/DelegateModal/AvatarUpload';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import headerCheckerboard from 'assets/logo/header-checkerboard-manager.svg';
import { UpdateManager } from './mutations';
import { ManagerModal_manager } from './__generated__/ManagerModal_manager.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.94) translateY(24px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '7vh',
    background: 'rgba(23, 25, 35, 0.18)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: 480,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: '86vh',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    animation: '$panelIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden'
  },
  cover: {
    position: 'relative',
    flexShrink: 0,
    height: 84,
    background: `
      url(${headerCheckerboard}) left top / auto 100% no-repeat,
      linear-gradient(135deg, #f4f7ff, #eef3ff)
    `,
    borderBottom: '1px solid #ededed'
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    cursor: 'pointer',
    color: '#74777f',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  avatarRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: -34,
    zIndex: 1
  },
  titleBlock: {
    padding: '12px 24px 18px',
    textAlign: 'center'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#74777f'
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 24px 24px'
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    margin: '8px 0 10px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 16
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 14,
    '& > $field': {
      marginBottom: 0
    },
    marginBottom: 16
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  input: {
    boxSizing: 'border-box',
    minHeight: 40,
    padding: '0 14px',
    fontSize: 14,
    color: theme.colors.primaryBlack,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    },
    '&::placeholder': {
      color: '#9ea2ad'
    }
  },
  errorBanner: {
    padding: '12px 14px',
    marginBottom: 14,
    fontSize: 13,
    color: '#b3261e',
    background: '#fef1f2',
    border: '1px solid #fadcdf',
    borderRadius: 8
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gridGap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #ededed'
  }
}));

type ManagerFields = {
  firstName: string;
  lastName: string;
  email: string;
  telephone: string;
  profileImageUrl?: string;
  profileImageToken?: string;
};

type Field = {
  label: string;
  placeholder: string;
  key: keyof ManagerFields;
  type?: InputTypes;
};

const contactFields: Field[] = [
  {
    label: 'Email',
    placeholder: 'e.g. example@fedex.com',
    key: 'email',
    type: 'email'
  },
  {
    label: 'Phone Number',
    placeholder: 'e.g. +44 1234 567890',
    key: 'telephone',
    type: 'tel'
  }
];

const nameFields: Field[] = [
  { label: 'First Name', placeholder: 'e.g. John', key: 'firstName' },
  { label: 'Last Name', placeholder: 'e.g. Smith', key: 'lastName' }
];

type Props = {
  isOpen: boolean;
  manager: ManagerModal_manager;
  refetch: () => void;
  onClose: () => void;
};

const ManagerModal = ({ isOpen, manager, refetch, onClose }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  useModalRegistration(isOpen);

  const managerToFields = (): ManagerFields => ({
    firstName: manager.firstName,
    lastName: manager.lastName,
    email: manager.email,
    telephone: manager.telephone ?? '',
    profileImageUrl: manager.profileImageUrl ?? undefined
  });

  const [fields, setFields] = React.useState<ManagerFields>(managerToFields);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  // Reset the form whenever the modal opens
  React.useEffect(() => {
    if (!isOpen) return;
    setFields(managerToFields());
    setLoading(false);
    setError(undefined);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const setField = (key: keyof ManagerFields) => (value: string) =>
    setFields((f) => ({ ...f, [key]: value }));

  // Limitation: saving always submits the whole form as shown — there is no
  // changed-field tracking, so unchanged fields are resent too.
  const submit = () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    UpdateManager(
      {
        uuid: manager.uuid,
        firstName: fields.firstName,
        lastName: fields.lastName,
        jobTitle: manager.jobTitle,
        email: fields.email,
        telephone: fields.telephone,
        profileImageToken: fields.profileImageToken
      },
      (err: string) => {
        setError(err);
        setLoading(false);
      },
      () => {
        setLoading(false);
        refetch();
        onClose();
      }
    );
  };

  const renderField = ({ label, placeholder, key, type }: Field) => (
    <div className={classes.field} key={key}>
      <span className={classes.label}>{label}</span>
      <CoreInput
        type={type ?? 'text'}
        placeholder={placeholder}
        value={fields[key] ?? ''}
        onChange={setField(key)}
        className={classes.input}
      />
    </div>
  );

  const displayName = `${fields.firstName} ${fields.lastName}`.trim();

  return (
    <div className={classes.overlay} onMouseDown={onClose}>
      <div
        className={classes.panel}
        onMouseDown={(evt) => evt.stopPropagation()}
      >
        <div className={classes.cover}>
          <div className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </div>
        <div className={classes.avatarRow}>
          <AvatarUpload
            name={displayName || 'Your Details'}
            url={fields.profileImageUrl}
            onUploaded={(token, url) =>
              setFields((f) => ({
                ...f,
                profileImageToken: token,
                profileImageUrl: url
              }))
            }
          />
        </div>
        <div className={classes.titleBlock}>
          <div className={classes.title}>{displayName || 'Your Details'}</div>
          <div className={classes.subtitle}>Update your details below</div>
        </div>
        <div className={classes.body}>
          {error && (
            <div className={classes.errorBanner}>
              Unable to save details: {error}
            </div>
          )}
          <div className={classes.sectionLabel}>Personal</div>
          <div className={classes.fieldGrid}>{nameFields.map(renderField)}</div>
          <div className={classes.sectionLabel}>Contact</div>
          {contactFields.map(renderField)}
        </div>
        <div className={classes.footer}>
          <Button archetype="flat" onClick={onClose}>
            Cancel
          </Button>
          <Button archetype="flatPrimary" disabled={loading} onClick={submit}>
            {loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default createFragmentContainer(ManagerModal, {
  manager: graphql`
    fragment ManagerModal_manager on Manager {
      uuid
      firstName
      lastName
      email
      jobTitle
      telephone
      profileImageUrl
    }
  `
});
